import { ReactComponent as BaseExpensesIcon } from '@ubeya/shared-web/assets/payroll-dollar.svg';
import { Button as BaseButton, Table as BaseTable, FlexColumn, header3SemiBold } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useArrayManagement from '@ubeya/shared-web/hooks/useArrayManagement';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { required } from '@ubeya/shared/utils/form';
import { FlexSpace, FormModal as BaseFormModal, DateField } from '@ubeya/shared-web/components';
import { responsiveFields } from '@ubeya/shared-web/style';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { isMobileOnly } from 'react-device-detect';

import moment from 'moment';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';

import useTranslatedOptions from '@ubeya/shared/hooks/useTranslatedOptions';
import { ContextMenu } from '@ubeya/shared-web/components';

import { CellWithArrow as BaseCellWithArrow } from '@ubeya/shared-web/components';
import { groupWages, wageCellFormatter } from 'shared/utils/wages';
import RatesFields from './RatesFields';
import { DEFAULT_POSITION_RATE_EFFECTIVE_DATE } from 'settings/constants';

const STATUS = { past: 'pastWage', future: 'futureWage' };

const Wrapper = styled.div`
  white-space: break-spaces;
`;

const CellWithArrow = styled(BaseCellWithArrow)`
  white-space: break-spaces;
`;

const Container = styled(FlexSpace)`
  ${responsiveFields.mobile};
`;

const FormModal = styled(BaseFormModal)`
  ${({ $isMobileOnly }) =>
    $isMobileOnly &&
    css`
      max-height: 65vh;
    `}
`;

const PositionWageModal = ({
  isFlatWage,
  item,
  onClose,
  onConfirm,
  validate,
  showDefaultPositionEffectiveDate = true
}) => {
  const { t } = useContext(ConfigContext);
  const { wage, payrollWage } = item || {};

  const wageInitialValues = useMemo(
    () => ({
      ...item,
      effectiveDate:
        item.effectiveDate || (showDefaultPositionEffectiveDate ? DEFAULT_POSITION_RATE_EFFECTIVE_DATE : null),

      wage: {
        ...wage,
        flatHours: !wage || wage.flatHours === null || wage.flatHours === 0 ? '' : wage.flatHours,
        hourlyWage: !wage || wage.hourlyWage === null || wage.hourlyWage === 0 ? '' : wage.hourlyWage,
        flatWage: !wage || wage.flatWage === null || wage.flatWage === 0 ? '' : wage.flatWage
      },
      payrollWage: {
        ...payrollWage,
        flatHours:
          !payrollWage || payrollWage.flatHours === null || payrollWage.flatHours === 0 ? '' : payrollWage.flatHours,
        hourlyWage:
          !payrollWage || payrollWage.hourlyWage === null || payrollWage.hourlyWage === 0 ? '' : payrollWage.hourlyWage,
        flatWage:
          !payrollWage || payrollWage.flatWage === null || payrollWage.flatWage === 0 ? '' : payrollWage.flatWage
      }
    }),
    [item, showDefaultPositionEffectiveDate, wage, payrollWage]
  );
  const initialValues = useMemo(() => ({ effectiveDate: moment.utc(), ...wageInitialValues }), [wageInitialValues]);

  const handleSubmit = useCallback(
    (values) =>
      onConfirm({
        ...values,
        effectiveDate: moment(values.effectiveDate).format(API_DATE_FORMAT)
      }),
    [onConfirm]
  );

  return (
    <FormModal
      initialValues={initialValues}
      onSubmit={handleSubmit}
      size="small"
      validate={validate}
      onClose={onClose}
      title={`${item?.id ? t('editWage') : t('addWage')}`}
      confirmText={t('save')}
      showCancelButton
      $isMobileOnly={isMobileOnly}
      disabledOnPristine={false}>
      <Container>
        <RatesFields isFlatWage={isFlatWage} />
        <DateField name="effectiveDate" title={t('effectiveFrom')} type="date" validate={required} required usePortal />
      </Container>
    </FormModal>
  );
};

const PositionWithDrawerRenderer = ({ rowData: { subRows, wageStatus, positionId }, isExpended, toggleRow }) => {
  const { t } = useContext(ConfigContext);
  const { mappedPositions } = usePositions();

  const value = t(STATUS[wageStatus]) || '';

  if (!subRows || subRows.length < 1) {
    return <Wrapper>{mappedPositions?.[positionId]?.slug || ''}</Wrapper>;
  }

  return (
    <CellWithArrow isExpended={isExpended} toggleRow={toggleRow} multiline>
      {value}
    </CellWithArrow>
  );
};

const PositionWageActionRenderer = ({
  rowData,
  rowData: { id, wageStatus, positionId },
  columnData: { handleDelete, handleAdd, handleEdit }
}) => {
  const { t } = useContext(ConfigContext);

  const options = useTranslatedOptions([
    { label: t('edit'), onClick: () => handleEdit(rowData), visible: id },
    { label: t('delete'), onClick: () => handleDelete(id), visible: id },
    {
      label: t('add'),
      onClick: () => handleAdd({ positionId }),
      visible: !wageStatus && !!positionId
    }
  ]);

  return <ContextMenu options={options} usePortal />;
};

const Action = styled.div`
  flex: 1;
`;

const Button = styled(BaseButton)`
  margin: 20px 0;
`;

const Table = styled(BaseTable)`
  overflow-x: auto;
  .row.past {
    background: ${({ theme }) => theme.colors.grey1};
    color: ${({ theme }) => theme.colors.grey3};
  }
  .row.future {
    color: ${({ theme }) => theme.colors.grey3};
  }
`;

const TableWrapper = styled(FlexColumn)`
  padding: 10px 40px;
`;

const icon = css`
  margin-right: 10px;
`;

const ExpensesIcon = styled(BaseExpensesIcon)`
  ${icon};
  g {
    stroke: ${({ theme }) => theme.colors.grey4};
  }
`;

const MainLabel = styled.div`
  display: flex;
  align-items: center;
  ${header3SemiBold};
  font-size: 16px;
  background: ${({ theme }) => theme.colors.grey1};
  color: ${({ theme }) => theme.colors.black};
  margin: 20px 20px 0 20px;
  line-height: 1.78;
  height: 40px;
  padding: 3px 36px 5px 16px;
`;

const RatesWithEffective = ({ isFlatWage, selectedRate, showBillRate }) => {
  const [editedWage, setEditedWage] = useState();
  const { t, currency, dateFormat } = useContext(ConfigContext);
  const { mappedPositions } = usePositions();
  const formattingMap = useFormattingMap();

  const myPrivileges = useContext(PrivilegesContext);

  const hasAmendPayRatePrivilege = !!myPrivileges[privileges.AMEND_PAY_RATE];

  const { data = [], handleDelete, handleAdd, handleEdit } = useArrayManagement(`wages`);

  const handleAddWage = useCallback((item) => {
    setEditedWage(item);
  }, []);

  const handleEditWage = useCallback((item) => {
    setEditedWage(item);
  }, []);

  const handleRowClick = useCallback(
    ({ rowData, rowData: { id, positionId } }) => (!id ? handleAddWage({ positionId }) : handleEditWage(rowData)),
    [handleAddWage, handleEditWage]
  );

  const wageFormatter = useCallback(
    ({ value, rowData, key, currency: withCurrency = true }) => {
      return wageCellFormatter({
        value,
        rowData,
        t,
        key,
        currency: withCurrency,
        isFlatWage,
        formattingMap,
        mappedPositions,
        showDefaultWages: false,
        includeZero: true
      });
    },
    [formattingMap, isFlatWage, mappedPositions, t]
  );

  const columns = useMemo(
    () => [
      {
        Header: '',
        dataKey: 'positionId',
        Cell: PositionWithDrawerRenderer,
        width: 150,
        captureRowClick: false
      },
      {
        Header: t('clientWage'),
        dataKey: 'wage.hourlyWage',
        formatter: (value, rowData) => wageFormatter({ value, rowData, key: 'hourlyWage' }),
        width: 80
      },

      {
        Header: t('specificClientPayRate'),
        dataKey: 'payrollWage.hourlyWage',
        formatter: (value, rowData) => wageFormatter({ value, rowData, key: 'hourlyWage' }),
        width: 80
      },

      ...(isFlatWage
        ? [
            {
              Header: t('globalWage'),
              dataKey: 'wage.flatWage',
              formatter: (value, rowData) => wageFormatter({ value, rowData, key: 'flatWage' }),
              width: 80
            },
            {
              Header: t('flatBillRateHoursLimit'),
              dataKey: 'wage.flatHours',
              formatter: (value, rowData) => wageFormatter({ value, rowData, key: 'flatHours', currency: false }),
              width: 80
            },

            {
              Header: t('flatWage'),
              dataKey: 'payrollWage.flatWage',
              formatter: (value, rowData) => wageFormatter({ value, rowData, key: 'flatWage' }),
              width: 80
            },
            {
              Header: t('flatHours'),
              dataKey: 'payrollWage.flatHours',
              formatter: (value, rowData) => wageFormatter({ value, rowData, key: 'flatHours', currency: false }),
              width: 80
            }
          ]
        : []),

      {
        Header: t('effectiveFrom'),
        dataKey: 'effectiveDate',
        formatter: (value) => {
          if (!value || value === DEFAULT_POSITION_RATE_EFFECTIVE_DATE) {
            return '-';
          }
          return moment(value).format(dateFormat);
        }
      }
    ],
    [dateFormat, isFlatWage, t, wageFormatter]
  );

  const defaultExtendsRows = data.length <= 2 ? { 0: true } : { 0: false };
  const showDefaultPositionEffectiveDate = data.length === 0;

  const groupedWages = groupWages({
    positions: [selectedRate?.positionId],
    data: data.map((current) => ({
      ...current,
      positionId: selectedRate?.positionId
    }))
  });

  const handleAddFirstPositionWage = () => {
    handleAddWage({ positionId: selectedRate.positionId });
  };

  const validate = useCallback(
    ({ effectiveDate = '' }) => {
      const errors = {};
      const wagesEffectiveDates = new Set(
        data
          .filter((wage) => wage.id !== editedWage?.id)
          .map((wage) => moment(wage.effectiveDate).format(API_DATE_FORMAT))
      );
      if (effectiveDate && wagesEffectiveDates.has(moment(effectiveDate).format(API_DATE_FORMAT))) {
        errors.effectiveDate = t('overlappingDateEffectiveFromForm');
      }
      return errors;
    },
    [data, editedWage?.id, t]
  );

  return (
    <>
      <MainLabel>
        <ExpensesIcon />
        {t('positionModalHourlyRates')} ({currency})
      </MainLabel>

      <TableWrapper>
        {data.length === 0 ? (
          <Action>
            <Button type="button" secondary narrow onClick={handleAddFirstPositionWage}>
              {` + ${t('addPositionWage')} `}
            </Button>
          </Action>
        ) : (
          <Table
            autoHeight
            gridHeight={400}
            headerRowHeight={50}
            columns={columns}
            defaultExtendsRows={defaultExtendsRows}
            data={groupedWages}
            rowStyle={({ wageStatus }) => wageStatus}
            onRowClick={hasAmendPayRatePrivilege ? handleRowClick : null}
            actionRenderer={({ rowData }) =>
              hasAmendPayRatePrivilege && (
                <PositionWageActionRenderer
                  rowData={rowData}
                  columnData={{
                    handleAdd: handleAddWage,
                    handleDelete,
                    handleEdit: handleEditWage
                  }}
                />
              )
            }
            shadow={false}
          />
        )}
      </TableWrapper>

      {editedWage && (
        <PositionWageModal
          showDefaultPositionEffectiveDate={showDefaultPositionEffectiveDate}
          validate={validate}
          isFlatWage={isFlatWage}
          showBillRate={showBillRate}
          onClose={() => setEditedWage()}
          onConfirm={(item) => {
            if (item.id) {
              handleEdit(item.id, item);
            } else {
              handleAdd(item);
            }
            setEditedWage();
          }}
          item={editedWage}
        />
      )}
    </>
  );
};

export default RatesWithEffective;
